import { CoreStore, handleStoreState } from "../RESTStore/CoreStore";
import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { projectsDataStore } from "../ProjectsStore";
import { userInfoStore } from "../UserInfoStore";
import md5 from "blueimp-md5";
import { API_URL } from "General/APIEndpoints";
import { API } from "General/API";
import Axios, { AxiosProgressEvent, CancelTokenSource } from "axios";
import { sidebarStore } from "Components/Core/Sidebar/SidebarStore";
import printJS from "print-js";
import { download } from "utils/download";
import { waitFor } from "utils/waitFor";

// to use different url for fetching file from different modules
export type PdfViewerOptionsMode = "TENDER" | "TENDER_GUEST" | "MEETINGS";
export interface PdfViewerOptions {
  page?: number;
  fileName: string;
  mode?: PdfViewerOptionsMode;
  tenderCallId?: number; // used when mode == 'TENDER
  canDownload?: boolean;
  isExternalFile?: boolean;
  linkedFolderId?: number;
  workListId?: number;
  archivedFileId?: number;
  splitView?: boolean;
  /* Triggers find with specified string after document is loaded */
  initialSearchQuery?: string;
  callbackOnClose?: () => void; // function that is called when viewer is closed. Used in workflow module...
}

interface PreservedOptions extends Partial<PdfViewerOptions> {
  fileId?: number;
}

const defaultOptions: PdfViewerOptions = {
  page: 1,
  fileName: "",
  canDownload: false,
};

class ApryseViewerStore extends CoreStore {
  isVisible: boolean = false;
  isVisibleSplitView: boolean;
  pageNumber: number;
  progress = 0;
  fileUrl = "";
  fileName = "";
  fileId: number = undefined;
  defaultRotate: number;
  options: PdfViewerOptions;
  password: string = "";
  fileHasPassword: boolean = false;
  isPasswordModalVisible: boolean = false;
  filePasswordAccepted: boolean = false;
  filePassword: string = "";
  incorrectPasswordErrorCounter: number = 0;
  isExternalFile: boolean = false;
  linkedFolderId: number = null;
  timeoutId: number = undefined;
  hasAnnotations: boolean = false;
  isClosing: boolean = false;

  private callbackOnClose: Function;
  private _preservedOptions: PreservedOptions = {};
  private cancelTokenSource: CancelTokenSource = Axios.CancelToken.source();

  constructor() {
    super();

    makeObservable(this, {
      isVisible: observable,
      isVisibleSplitView: observable,
      pageNumber: observable,
      progress: observable,
      fileUrl: observable,
      fileName: observable,
      fileId: observable,
      defaultRotate: observable,
      options: observable,
      fileHasPassword: observable,
      isPasswordModalVisible: observable,
      filePasswordAccepted: observable,
      filePassword: observable,
      incorrectPasswordErrorCounter: observable,
      hasAnnotations: observable,
      isClosing: observable,
      clear: action.bound,
      closeModal: action.bound,
      toggleModal: action.bound,
      hideSplitView: action.bound,
      fetchPdfFile: action.bound,
      fetchFile: action.bound,
      setDownloadProgress: action.bound,
      getOptions: computed,
      fetch: action.bound,
      initViewer: action.bound,
      setHasAnnotations: action.bound,
    });
  }

  url(fileId: number) {
    if (this.isExternalFile) {
      return API_URL.LINKED.getWebViewerExternalLinkedFile(projectsDataStore.projectId, this.linkedFolderId, fileId);
    } else {
      return API_URL.VIEWER.getWebViewerFile(projectsDataStore.projectId, fileId);
    }
  }

  clear() {
    this.fileUrl = "";
    this.password = "";
    this.fileName = "";
    this.pageNumber = undefined;
    this.isVisible = false;
    this.isVisibleSplitView = false;
    this.progress = 0;
    this.defaultRotate = 0;
    this.options = undefined;
    this.cancelTokenSource = Axios.CancelToken.source();
    this.fileHasPassword = false;
    this.isPasswordModalVisible = false;
    this.filePasswordAccepted = false;
    this.filePassword = "";
    this.incorrectPasswordErrorCounter = 0;
    this.isExternalFile = false;
    this.linkedFolderId = null;
    this._preservedOptions = {};
    this.callbackOnClose = undefined;
    this.hasAnnotations = false;
    this.isClosing = false;
    // NOTE: don't clear fileId because annotationChange event can be triggered after viewer is closed
  }

  async closeModal(): Promise<void> {
    try {
      this.isClosing = true;
      // Need to wait for WebViewer annotationChange event to be triggered
      // for some folks who like to add annotations and then quickly close the viewer
      await waitFor(1500);
    } finally {
      runInAction(() => {
        this.isVisible = false;
        this.isClosing = false;
      });
      if (this.callbackOnClose) {
        this.callbackOnClose();
      }
      this.reset();
    }
  }

  toggleModal() {
    this.isVisible = !this.isVisible;
    if (!this.isVisible) {
      // if (this.state === 'LOADING') {
      //   this.cancelFetch();
      // }
      if (this.callbackOnClose) {
        this.callbackOnClose();
      }
      this.reset();
    }
  }

  hideSplitView() {
    this.isVisibleSplitView = false;
    this.clear();
    this.reset();
    if (window.innerWidth > 1025) {
      sidebarStore.setSidebarCollapse(false);
    }
  }

  cancelFetch = () => {
    this.cancelTokenSource.cancel();
  };

  setPassword() {
    this.password = md5(`${userInfoStore.id}${userInfoStore.loginInfo.loginTime}`);
  }

  @handleStoreState()
  async fetch(fileId: number, options: PdfViewerOptions) {
    options = { ...defaultOptions, ...options };
    if (this.isVisibleSplitView) {
      this.hideSplitView();
    }
    try {
      if (options.splitView) {
        this.isVisibleSplitView = true;
        if (window.innerWidth < 1820) {
          sidebarStore.setSidebarCollapse(true);
        }
      } else {
        this.isVisible = true;
      }
      if (typeof options.callbackOnClose === "function") {
        this.callbackOnClose = options.callbackOnClose;
      }
      await this.initViewer(fileId, options);
    } catch (err) {
      throw err;
    }
  }

  reFetch() {
    const { fileId, ...options } = this._preservedOptions;
    return this.fetch(fileId, options as PdfViewerOptions);
  }

  @handleStoreState()
  async initViewer(fileId: number, options: PdfViewerOptions) {
    this.options = options;
    this._preservedOptions = { fileId, ...options };
    try {
      this.fileId = fileId;
      if (options.isExternalFile) {
        this.isExternalFile = options.isExternalFile;
        this.linkedFolderId = options.linkedFolderId;
      }
      this.pageNumber = options.page || defaultOptions.page;
      this.setPassword();
      this.fileName = options.fileName ? `"${options.fileName}"` : defaultOptions.fileName;
    } catch (error) {
      if (error.response?.status === 409) {
        if (error.response.data.code === 4) {
          runInAction(() => {
            this.fileHasPassword = true;
            this.isPasswordModalVisible = true;
            this.incorrectPasswordErrorCounter++;
          });
        }
      }
      throw error;
    }
  }

  async fetchFile(fileId: number, options: PdfViewerOptions): Promise<File> {
    try {
      if (options.mode?.startsWith("TENDER")) {
        if (options.mode === "TENDER_GUEST") {
          return await API.getBlobByPost(API_URL.TENDER_GUEST.getFileDownload_1(options.tenderCallId), [fileId]);
        } else {
          return await API.getBlobByPost(
            API_URL.TENDER.getFileDownload(projectsDataStore.projectId, options.tenderCallId),
            [fileId]
          );
        }
      } else if (options.archivedFileId) {
        return await API.getBlob(
          API_URL.FILES.getArchiveFileById(projectsDataStore.projectId, fileId, options.archivedFileId)
        );
      } else {
        const formBody = {
          files: [{ id: this.fileId, state: "ACTIVE" }],
          withDirectories: false,
          withQR: projectsDataStore.isQRForce(),
        };
        return await API.getBlobByPost(API_URL.FILES.getDownloadWizard2Response(projectsDataStore.projectId), formBody);
      }
    } catch (error) {
      throw error;
    }
  }

  async fetchPdfFile(fileId: number = this.fileId, options: PdfViewerOptions = this.options) {
    try {
      let url: string;
      if (options.mode?.startsWith("TENDER")) {
        if (options.mode === "TENDER_GUEST") {
          url = API_URL.VIEWER.getCallGuestWebViewerFile(options.tenderCallId, fileId);
        } else {
          url = API_URL.TENDERVIEWER.getTenderWebViewerFile(projectsDataStore.projectId, options.tenderCallId, fileId);
        }
      } else if (options.archivedFileId) {
        url = API_URL.VIEWER.getArchivedWebViewerFile(projectsDataStore.projectId, fileId, options.archivedFileId);
      } else {
        url = this.url(fileId);
      }
      const blob = await API.getBlob(
        url,
        { password: this.filePassword ? this.filePassword : undefined },
        {
          onDownloadProgress: (progress) => this.setDownloadProgress(progress),
          cancelToken: this.cancelTokenSource.token,
          responseType: "blob",
        }
      );
      if (blob && this.fileHasPassword) {
        runInAction(() => {
          this.filePasswordAccepted = true;
          this.isPasswordModalVisible = false;
        });
      }
      return blob;
    } catch (error) {
      throw error;
    } finally {
      this.setDownloadProgress({ loaded: 1, total: 1, bytes: 1, lengthComputable: true });
    }
  }

  setDownloadProgress({ loaded, total }: AxiosProgressEvent) {
    this.progress = Math.round((loaded / total) * 100);
  }

  async downloadFileBlob({ includeAnnotations }: { includeAnnotations: boolean }) {
    try {
      let file: File;
      const params = this.options.archivedFileId ? { archived: true } : {};
      const fileId = this.options.archivedFileId ? this.options.archivedFileId : this.fileId;
      if (includeAnnotations) {
        file = await API.getBlob(
          API_URL.VIEWER.getWebViewerAnnotatedFile(projectsDataStore.projectId, fileId),
          params
        );
      } else {
        file = await this.fetchFile(fileId, this.options);
      }
      return file;
    } catch (error) {
      throw error;
    }
  }

  async downloadFile({ includeAnnotations }: { includeAnnotations: boolean }) {
    try {
      const file = await this.downloadFileBlob({ includeAnnotations });
      download(file, file.name);
    } catch (error) {
      throw error;
    }
  }

  async printPdf({ includeAnnotations }: { includeAnnotations: boolean }) {
    try {
      const file = await this.downloadFileBlob({ includeAnnotations });
      const objectUrl = URL.createObjectURL(file);
      printJS({
        printable: objectUrl,
        type: "pdf",
        onPrintDialogClose: () => URL.revokeObjectURL(objectUrl),
      });
    } catch (error) {
      throw error;
    }
  }

  getAnnotationConfig(): { url: string; params: Record<string, unknown> | null } {
    if (this.options?.mode?.startsWith("TENDER")) {
      return {
        url: API_URL.WEBVIEWER_ANNOTATIONS.getWebViewerCftAnnotations(
          projectsDataStore.projectId,
          this.options.tenderCallId,
          this.fileId
        ),
        params: null,
      };
    }

    const isArchived = !!this.options?.archivedFileId;
    const fileId = isArchived ? this.options.archivedFileId : this.fileId;

    return {
      url: API_URL.WEBVIEWER_ANNOTATIONS.getWebViewerAnnotations(projectsDataStore.projectId, fileId),
      params: isArchived ? { archived: true } : null,
    };
  }

  async changeAnnotations(xmlString: string, action: "add" | "modify" | "delete") {
    try {
      const { url, params } = this.getAnnotationConfig();
      switch (action) {
        case "add":
          await API.newData(url, xmlString, params, { responseType: "text", headers: { "Content-Type": "text/xml" } });
          break;
        case "modify":
          await API.editData(url, xmlString, params, { responseType: "text", headers: { "Content-Type": "text/xml" } });
          break;
        case "delete":
          await API.deleteData(url, params, { body: xmlString, headers: { "Content-Type": "text/xml" } });
          break;
      }
    } catch (error) {
      throw error;
    }
  }

  async getAnnotationsXml(): Promise<string> {
    try {
      const { url, params } = this.getAnnotationConfig();
      return await API.getData<string>(url, params, { responseType: "text" });
    } catch (error) {
      throw error;
    }
  }

  setHasAnnotations(hasAnnotations: boolean) {
    this.hasAnnotations = hasAnnotations;
  }

  get getOptions() {
    if (this.options) {
      return Object.assign({}, this.options);
    }
    return undefined;
  }

  get isFullMode() {
    if (!this.options || this.isExternalFile) {
      return false;
    }
    // for MEETINGS, TENDERS, file from external linked projects mode annotations should be disabled.
    // if another mode will be added where annotations are allowed - this check should be adjusted
    return !this.options.mode && userInfoStore.role !== "GUEST" && projectsDataStore.currentProject?.easyViewerFullMode;
  }

  get isReadOnlyMode() {
    return projectsDataStore.isArchived || userInfoStore.role !== "GUEST";
  }
}

const apryseViewerStore = new ApryseViewerStore();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).apryseViewerStore = ApryseViewerStore;

export { apryseViewerStore, ApryseViewerStore };
